import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';

import { reverseFormatDate, todayDate } from '../../../utils/dateFunctions';

import pdf from '../../../commonStyles/pdf.module.css';
import local from './formStyles/generateForm.module.css';

const GenerateStandardForm = ({
    surveyModel,
    form,
    row,
    currentParticipant,
    participantsAdviser,
    onPdfGenerated,
    onClose
}) => {
    const templateRef = useRef(null);

    const [completedDate, setCompletedDate] = useState('');
    const [groupedByPage, setGroupedByPage] = useState({});

    // USE EFFECTS
    useEffect(() => {
        const date = !row.lastUpdated
            ? reverseFormatDate(todayDate())
            : reverseFormatDate(row.lastUpdated);
        setCompletedDate(date);

        const pages = form.data.pages;
        // calculate what page each question is on
        const formData = surveyModel.getPlainData().map((data) => {
            const page = pages.find((page) =>
                page.elements.some((entry) => entry.name === data.name)
            );
            return {
                ...data,
                page: page.title || page.name
            };
        });

        const configData = formData.reduce((acc, item) => {
            acc[item.page] = [...(acc[item.page] || []), item];
            return acc;
        }, {});

        setGroupedByPage(configData);
    }, []);

    useEffect(() => {
        if (!templateRef?.current || !Object.keys(groupedByPage).length) return;
        generatePdf();
    }, [groupedByPage]);

    // FNs
    const createFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;

        doc.setFontSize(8);

        Array.from({ length: pageCount }, (_, i) => i + 1).forEach((pageNumber) => {
            doc.setPage(pageNumber);
            const footerText =
                `    Page ${pageNumber} of ${pageCount} ` +
                ' '.repeat(110) +
                ` Printed on ${format(new Date(), 'EEEE, MMMM dd, yyyy HH:mm')} Document ID : ${
                    form.code
                }`;

            doc.text(footerText, 1, pageSize.height - 5, { align: 'left' });
        });
    };

    const generatePdf = () => {
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.html(templateRef.current, {
            async callback(doc) {
                createFooter(doc);
                doc.save(form.name);
                onPdfGenerated(true);
                onClose && onClose();
            },
            autoPaging: 'text',
            margin: [10, 5, 10, 5],
            width: 210,
            windowWidth: 1050
        });
    };

    // RENDER
    return (
        <div className={local.previewWrapper}>
            <div ref={templateRef} id="formTemplateContainer" data-testid="formTemplateContainer">
                <div className={pdf.column}>
                    <div className={pdf.row}>
                        <span>Form Title</span>
                        <span>{surveyModel.title || form.name}</span>
                    </div>
                    <div className={pdf.row}>
                        <span>Form ID</span>
                        <span>{form.code}</span>
                    </div>
                    <div className={pdf.row}>
                        <span>Participant Name</span>
                        <span>
                            {currentParticipant.firstName} {currentParticipant.lastName}
                        </span>
                    </div>
                    <div className={pdf.row}>
                        <span>Participant ID</span>
                        <span>{currentParticipant.ptCode}</span>
                    </div>
                    <div className={pdf.row}>
                        <span>Adviser Name</span>
                        <span>{`${participantsAdviser.firstName || ''} ${participantsAdviser.lastName || ''}`}</span>
                    </div>
                    <div className={pdf.row}>
                        <span>Date Complete</span>
                        <span>{completedDate}</span>
                    </div>
                </div>
                <div className={pdf.section}>
                    {Object.entries(groupedByPage).map(([pageTitle, questions]) => (
                        <div key={pageTitle}>
                            <h2 className={pdf.pageTitle}>{pageTitle}</h2>
                            <div className={pdf.questionAnswerBlock}>
                                {questions.map((el) => (
                                    <div key={el.title} className={pdf.questionAnswerRow}>
                                        <span className={pdf.key}>
                                            {el.title || 'No question title'}
                                        </span>

                                        <span className={pdf.value}>
                                            {typeof el.displayValue === 'string'
                                                ? !el.displayValue.includes('data:image/png')
                                                    ? el.displayValue
                                                    : '[Question Type is an Image]'
                                                : '[Question Type is not Text]'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

GenerateStandardForm.propTypes = {
    surveyModel: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
    row: PropTypes.object.isRequired,
    currentParticipant: PropTypes.object.isRequired,
    participantsAdviser: PropTypes.object.isRequired,
    onPdfGenerated: PropTypes.func.isRequired,
    onClose: PropTypes.func
};

export default GenerateStandardForm;
